import { useTranslation } from 'react-i18next';

import { Callout } from '@/shared/components/feedback/Callout/Callout';
import { EmptyState } from '@/shared/components/feedback/EmptyState/EmptyState';
import { Skeleton } from '@/shared/components/feedback/Skeleton/Skeleton';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { JsonEditor } from '@/shared/components/structured/JsonEditor/JsonEditor';
import { Button } from '@/shared/components/ui/Button/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';

import { useBillingEventPayloadModalLogic } from './useBillingEventPayloadModalLogic';

/**
 * What the store sent, for one delivery.
 *
 * Presentation only; every decision is in `useBillingEventPayloadModalLogic`
 * (U5).
 *
 * The payload goes through the kit's `JsonEditor` in read-only mode rather than
 * a `<pre>` or a textarea: U0 forbids a raw JSON blob where a control exists,
 * and this one already numbers its lines, marks the direction (`dir="ltr"` —
 * JSON is code, and code has one direction, U7) and can be formatted and
 * copied. `DialogBody` is the kit's own scroller, so a thousand-line payload
 * scrolls **inside** the dialog and never the page behind it (U0).
 */
export function BillingEventPayloadModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('payments');
  const view = useBillingEventPayloadModalLogic({
    eventId: typeof modalData?.['eventId'] === 'string' ? modalData['eventId'] : '',
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
    >
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>{t('events.payload.title')}</DialogTitle>
          <DialogDescription>
            {view.subtitle === '' ? t('events.payload.description') : view.subtitle}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>{renderBody()}</DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('error.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  function renderBody() {
    if (view.isLoading) {
      return (
        <div aria-busy>
          <Skeleton className="h-64 w-full" />
          <span className="sr-only">{t('events.payload.loading')}</span>
        </div>
      );
    }

    if (view.error !== null) {
      // A 403 here means the grant went away since the page loaded — the api
      // being right, so it gets the sentence that names the permission rather
      // than a raw server message (P20b).
      return (
        <Callout tone="danger" title={t('events.payload.title')}>
          <div className="flex flex-col items-start gap-sm">
            <p>{refusalMessage()}</p>
            {/* Neither refusal is worth retrying: a 403 will refuse again until
                somebody restores the grant, and a row that is gone stays gone.
                Only an unexplained failure gets the re-read (P23b2). */}
            {view.isRestricted || view.isGone ? (
              <Button variant="secondary" onClick={onCancel}>
                {t('error.close')}
              </Button>
            ) : (
              <Button variant="secondary" onClick={view.refetch}>
                {t('error.reread')}
              </Button>
            )}
          </div>
        </Callout>
      );
    }

    if (view.isEmpty) {
      return <EmptyState title={t('events.payload.empty')} />;
    }

    return (
      <JsonEditor
        label={t('events.payload.title')}
        value={view.payload}
        isReadOnly
        onValueChange={NOOP}
      />
    );
  }

  /**
   * The two refusals this dialog can meet get their own sentence; anything else
   * shows the api's own, rather than a translation this module guessed at
   * (`payment-error.util`'s rule, applied without borrowing its order-shaped
   * return).
   */
  function refusalMessage(): string {
    if (view.isRestricted) {
      return t('events.payload.restricted');
    }

    return view.isGone ? t('error.eventNotFound') : (view.error?.message ?? t('error.generic'));
  }
}

/** The editor is a viewer here — it is `isReadOnly`, so nothing ever calls this. */
const NOOP = () => undefined;
